import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useConfigs } from "../api/configs";
import { useSources, useUploadSource } from "../api/sources";
import { useSpecs } from "../api/validation";
import {
  downloadItemBundle,
  downloadJobBundle,
  useGenerate,
} from "../api/generation";
import { errorMessage } from "../api/client";
import type {
  FileFormat,
  GenerationJob,
  GenerationSource,
  Separator,
  SourceKind,
} from "../api/types";
import { Button } from "../components/Button";
import { Select } from "../components/Select";
import { TextField } from "../components/TextField";
import { Badge } from "../components/Badge";
import { Spinner } from "../components/Spinner";
import { Dropzone } from "../components/Dropzone";
import { useToast } from "../components/Toast";
import { HelpTip } from "../components/HelpTip";
import { OutputValidationView } from "../components/validation/OutputValidationView";

const OUTPUT_FORMATS: { value: FileFormat; labelKey: string }[] = [
  { value: "DELIMITED", labelKey: "generate.formats.delimited" },
  { value: "EXCEL", labelKey: "generate.formats.excel" },
  { value: "XML", labelKey: "generate.formats.xml" },
  { value: "ACCESS2", labelKey: "generate.formats.access2" },
  { value: "ACCESS7", labelKey: "generate.formats.access7" },
];

const SEPARATORS: { value: Separator; labelKey: string }[] = [
  { value: "COMMA", labelKey: "generate.separators.comma" },
  { value: "TAB", labelKey: "generate.separators.tab" },
  { value: "PIPE", labelKey: "generate.separators.pipe" },
];

type SourceChoice = "synthetic" | string; // "synthetic" or a SourceUpload id

export function GeneratePage() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();

  const configsQ = useConfigs();
  const sourcesQ = useSources();
  const specsQ = useSpecs();
  const uploadSource = useUploadSource();
  const generate = useGenerate();

  const [configId, setConfigId] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [sourceChoice, setSourceChoice] = useState<SourceChoice>("synthetic");
  const [nSamples, setNSamples] = useState<number>(3);
  const [formats, setFormats] = useState<Set<FileFormat>>(new Set(["DELIMITED"]));
  const [separator, setSeparator] = useState<Separator>("COMMA");
  const [runSql, setRunSql] = useState(true);
  const [validate, setValidate] = useState(false);
  const [specId, setSpecId] = useState<string>(""); // "" ⇒ auto-detect
  const [job, setJob] = useState<GenerationJob | null>(null);

  const sources = sourcesQ.data ?? [];
  const selectedSource = sources.find((s) => s.id === sourceChoice);

  const toggleFormat = (f: FileFormat) =>
    setFormats((prev) => {
      const next = new Set(prev);
      if (next.has(f)) next.delete(f);
      else next.add(f);
      return next;
    });

  const buildSource = (): GenerationSource => {
    if (sourceChoice === "synthetic") return "synthetic";
    if (selectedSource?.kind === "csv_zip") return { csv_zip_id: sourceChoice };
    return { output_mdb_id: sourceChoice };
  };

  const handleUpload = async (file: File | undefined, kind: SourceKind) => {
    if (!file) return;
    try {
      const up = await uploadSource.mutateAsync({ file, kind });
      toast.success(t("generate.uploaded", { name: up.filename }));
      setSourceChoice(up.id);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const guessKind = (file: File): SourceKind =>
    file.name.toLowerCase().endsWith(".zip") ? "csv_zip" : "output_mdb";

  const separatorOptions = SEPARATORS.map((s) => ({ value: s.value, label: t(s.labelKey) }));

  const run = async () => {
    if (!configId) {
      toast.error(t("generate.pickConfig"));
      return;
    }
    if (formats.size === 0) {
      toast.error(t("generate.pickFormat"));
      return;
    }
    try {
      const result = await generate.mutateAsync({
        name: name.trim() || undefined,
        config_id: configId,
        source: buildSource(),
        formats: [...formats],
        n_samples: nSamples,
        run_sql: runSql,
        validate,
        spec_id: validate ? specId || null : null,
      });
      setJob(result);
      if (result.status === "completed") toast.success(t("generate.complete"));
      else if (result.status === "failed") toast.error(t("generate.failed"));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const wantsSeparator = formats.has("DELIMITED");

  return (
    <div className="page page--wide stack">
      <div className="row row--between">
        <div>
          <h1 style={{ marginBottom: 4 }}>{t("generate.title")}</h1>
          <p className="muted" style={{ margin: 0 }}>
            {t("generate.subtitle")}
          </p>
        </div>
        <Link to="/generate/jobs" className="btn btn--ghost">
          {t("generate.jobHistory")}
        </Link>
      </div>

      <div className="split-cols">
        {/* ── config + source ── */}
        <div className="card card--pad-sm stack">
          <h3 style={{ margin: 0 }}>{t("generate.step1")}</h3>

          <Select
            label={t("generate.config")}
            value={configId}
            onChange={(e) => setConfigId(e.target.value)}
            hint={
              configsQ.isLoading
                ? t("generate.loadingConfigs")
                : (configsQ.data?.length ?? 0) === 0
                  ? t("generate.noConfigsHint")
                  : undefined
            }
          >
            <option value="">{t("generate.selectConfig")}</option>
            {(configsQ.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.file_format ? ` (${c.file_format})` : ""}
              </option>
            ))}
          </Select>
          {(configsQ.data?.length ?? 0) === 0 && !configsQ.isLoading && (
            <Link to="/templates" className="btn btn--ghost btn--sm" style={{ alignSelf: "flex-start" }}>
              {t("generate.browseTemplates")}
            </Link>
          )}

          <Select
            label={t("generate.source")}
            value={sourceChoice}
            onChange={(e) => setSourceChoice(e.target.value)}
            hint={t("generate.sourceHint")}
          >
            <option value="synthetic">{t("generate.synthetic")}</option>
            {sources.map((s) => (
              <option key={s.id} value={s.id}>
                {s.filename} · {s.kind}
                {s.work_order ? ` · WO ${s.work_order}` : ""}
              </option>
            ))}
          </Select>

          {sourceChoice === "synthetic" && (
            <TextField
              label={t("generate.sampleRows")}
              type="number"
              min={1}
              max={50}
              value={nSamples}
              onChange={(e) => setNSamples(Math.max(1, Number(e.target.value) || 1))}
            />
          )}

          {selectedSource && selectedSource.warnings.length > 0 && (
            <div className="banner banner--warning">{selectedSource.warnings[0]}</div>
          )}

          <Dropzone
            label={t("generate.uploadTitle")}
            sublabel={t("generate.uploadSublabel")}
            hint={t("generate.uploadHint")}
            accept=".mdb,.zip,application/x-msaccess,application/zip"
            loading={uploadSource.isPending}
            loadingLabel={t("generate.uploading")}
            onFile={(file) => void handleUpload(file, guessKind(file))}
          />
        </div>

        {/* ── output + validation ── */}
        <div className="card card--pad-sm stack">
          <h3 style={{ margin: 0 }}>{t("generate.step2")}</h3>
          <div className="stack" style={{ gap: "var(--space-1)" }}>
            {OUTPUT_FORMATS.map((f) => (
              <label key={f.value} className="checkbox">
                <input
                  type="checkbox"
                  checked={formats.has(f.value)}
                  onChange={() => toggleFormat(f.value)}
                />
                {t(f.labelKey)}
              </label>
            ))}
          </div>

          {wantsSeparator && (
            <Select
              label={t("generate.delimiterSeparator")}
              value={separator}
              onChange={(e) => setSeparator(e.target.value as Separator)}
              options={separatorOptions}
              hint={t("generate.separatorHint")}
            />
          )}

          <label className="checkbox">
            <input type="checkbox" checked={runSql} onChange={(e) => setRunSql(e.target.checked)} />
            {t("generate.runTransforms")}
          </label>

          <h3 style={{ margin: "var(--space-2) 0 0" }}>
            {t("generate.step3")}{" "}
            <HelpTip label={t("generate.aboutValidation")}>
              {t("generate.aboutValidationBody")}
            </HelpTip>
          </h3>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={validate}
              onChange={(e) => setValidate(e.target.checked)}
            />
            {t("generate.validateOutput")}
          </label>
          {validate && (
            <Select
              label={t("generate.regulatorSpec")}
              value={specId}
              onChange={(e) => setSpecId(e.target.value)}
              hint={specsQ.isLoading ? t("generate.loadingSpecs") : t("generate.autoDetectHint")}
            >
              <option value="">{t("generate.autoDetect")}</option>
              {(specsQ.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.version ? ` (${s.version})` : ""}
                </option>
              ))}
            </Select>
          )}
        </div>
      </div>

      <div className="card card--pad-sm stack">
        <div className="row row--between row--wrap">
          <TextField
            label={t("generate.runName")}
            placeholder={t("generate.runNamePlaceholder")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ minWidth: 280 }}
          />
          <Button
            variant="primary"
            loading={generate.isPending}
            disabled={!configId || formats.size === 0}
            onClick={run}
            style={{ alignSelf: "flex-end" }}
          >
            {t("generate.runGeneration")}
          </Button>
        </div>
      </div>

      {generate.isPending && (
        <div className="center-pad">
          <Spinner size="lg" />
          <span>{t("generate.runningEngine")}</span>
        </div>
      )}

      {job && <JobResult job={job} onOpenFull={() => navigate(`/generate/jobs/${job.id}`)} />}
    </div>
  );
}

function JobResult({ job, onOpenFull }: { job: GenerationJob; onOpenFull: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);
  const item = job.items[0];

  const statusTone =
    job.status === "completed"
      ? "success"
      : job.status === "failed"
        ? "danger"
        : job.status === "partial"
          ? "warning"
          : "default";

  const download = async () => {
    setDownloading(true);
    try {
      await downloadJobBundle(job.id, job.name);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  const downloadItem = async () => {
    if (!item) return;
    try {
      await downloadItemBundle(job.id, item.id, item.label);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div className="card stack">
      <div className="row row--between row--wrap">
        <div className="row" style={{ gap: "var(--space-2)" }}>
          <h3 style={{ margin: 0 }}>{job.name}</h3>
          <Badge tone={statusTone}>{job.status}</Badge>
          {item && (
            <span className="faint">
              {t("generate.rowSummary", { sx: item.sx_row_count, qa: item.qa_row_count })}
            </span>
          )}
        </div>
        <div className="row">
          <Button variant="ghost" size="sm" onClick={onOpenFull}>
            {t("generate.openFullDetail")}
          </Button>
          {job.has_result && (
            <Button variant="primary" size="sm" loading={downloading} onClick={download}>
              {t("generate.downloadZip")}
            </Button>
          )}
        </div>
      </div>

      {job.error && <div className="banner banner--danger">{job.error}</div>}
      {job.warnings.map((w, i) => (
        <div key={i} className="banner banner--warning">
          {w}
        </div>
      ))}
      {item?.error && <div className="banner banner--danger">{item.error}</div>}

      {item && item.files.length > 0 && (
        <div className="stack" style={{ gap: "var(--space-2)" }}>
          {item.files.map((f) => (
            <FileCard key={f.name} name={f.name} format={f.format} size={f.size} sample={f.sample_text} />
          ))}
          <Button variant="ghost" size="sm" onClick={downloadItem} style={{ alignSelf: "flex-start" }}>
            {t("generate.downloadThisItem")}
          </Button>
        </div>
      )}

      {item?.validation && (
        <div className="card card--pad-sm">
          <OutputValidationView report={item.validation} />
        </div>
      )}
    </div>
  );
}

export function FileCard({
  name,
  format,
  size,
  sample,
}: {
  name: string;
  format: string;
  size: number;
  sample?: string | null;
}) {
  const prettySize = useMemo(() => {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }, [size]);

  return (
    <div className="card card--pad-sm stack" style={{ gap: "var(--space-2)" }}>
      <div className="row" style={{ gap: "var(--space-2)" }}>
        <strong className="mono">{name}</strong>
        <Badge tone="accent">{format}</Badge>
        <span className="faint">{prettySize}</span>
      </div>
      {sample && (
        <pre
          className="help-box"
          style={{ margin: 0, maxHeight: 220, overflow: "auto", whiteSpace: "pre" }}
        >
          {sample}
        </pre>
      )}
    </div>
  );
}
